import { useState } from "react";

export type SortKey = "latest" | "hot" | "rating";

interface FilterBarProps {
  genres: string[];
  regions: string[];
  years: number[];
  selectedGenre: string;
  selectedRegion: string;
  selectedYear: string;
  sort: SortKey;
  resultCount: number;
  onChange: (patch: {
    genre?: string;
    region?: string;
    year?: string;
    sort?: SortKey;
  }) => void;
  onReset: () => void;
}

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新" },
  { key: "hot", label: "热门" },
  { key: "rating", label: "评分" },
];

function ChipGroup({
  label,
  options,
  value,
  onPick,
}: {
  label: string;
  options: { key: string; label: string }[];
  value: string;
  onPick: (key: string) => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 py-3 border-b border-background-200 last:border-b-0">
      <span className="text-sm text-foreground-500 shrink-0 w-16 pt-1">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => onPick(opt.key)}
            className={`px-3 py-1.5 rounded-md text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer border ${
              value === opt.key
                ? "bg-primary-500 text-background-50 border-primary-500"
                : "bg-background-50 text-foreground-700 border-background-200 hover:border-primary-300"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({
  genres,
  regions,
  years,
  selectedGenre,
  selectedRegion,
  selectedYear,
  sort,
  resultCount,
  onChange,
  onReset,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const genreOptions = [{ key: "all", label: "全部" }, ...genres.map((g) => ({ key: g, label: g }))];
  const regionOptions = [{ key: "all", label: "全部" }, ...regions.map((r) => ({ key: r, label: r }))];
  const yearOptions = [{ key: "all", label: "全部" }, ...years.map((y) => ({ key: String(y), label: `${y}` }))];

  const hasFilter = selectedGenre !== "all" || selectedRegion !== "all" || selectedYear !== "all";

  return (
    <div className="bg-background-100 rounded-lg border border-background-200">
      <div className="flex items-center justify-between gap-3 p-3 md:p-4">
        <div className="flex items-center gap-2 text-sm">
          <i className="ri-filter-3-line text-foreground-500"></i>
          <span className="text-foreground-700">
            筛选结果 <strong className="text-accent-600 font-semibold">{resultCount}</strong> 部
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-1 p-1 rounded-full bg-background-50 border border-background-200">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => onChange({ sort: opt.key })}
                className={`px-3 py-1 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  sort === opt.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-500"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden h-9 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
          >
            <i className="ri-equalizer-line"></i>
            筛选
          </button>
          {hasFilter && (
            <button
              type="button"
              onClick={onReset}
              className="h-9 px-3 rounded-md text-sm text-secondary-600 hover:text-secondary-700 whitespace-nowrap flex items-center gap-1 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置
            </button>
          )}
        </div>
      </div>

      <div className={`px-3 md:px-4 border-t border-background-200 ${open ? "block" : "hidden"} lg:block`}>
        <div className="flex sm:hidden items-center gap-1 p-2 my-2 rounded-full bg-background-50 border border-background-200 w-fit">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange({ sort: opt.key })}
              className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                sort === opt.key ? "bg-primary-500 text-background-50" : "text-foreground-600"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <ChipGroup
          label="子类型"
          options={genreOptions}
          value={selectedGenre}
          onPick={(key) => onChange({ genre: key })}
        />
        <ChipGroup
          label="地区"
          options={regionOptions}
          value={selectedRegion}
          onPick={(key) => onChange({ region: key })}
        />
        <ChipGroup
          label="年份"
          options={yearOptions}
          value={selectedYear}
          onPick={(key) => onChange({ year: key })}
        />
        <div className="pb-4 lg:hidden">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="w-full h-10 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
          >
            查看 {resultCount} 部作品
          </button>
        </div>
      </div>
    </div>
  );
}